import { NotificationModule } from '@application/enums/notification-module-enum';
import { EventNotificationType, TaskNotificationType, ActivityNotificationType, NotificationTypeDisplay } from '@application/enums/notification-type-enum';
import { PriorityEnum } from '@application/enums/priority-enum';
// Runtime-triggered notifications that don't need reminders (triggered immediately on action)
// Note: These notifications are triggered immediately when the action occurs and don't support scheduled reminders
const RUNTIME_TRIGGERED_NOTIFICATIONS = [
    EventNotificationType.EventCreated,
    EventNotificationType.EventRescheduled,
    TaskNotificationType.TaskAssigned,
    TaskNotificationType.TaskCompleted
    // Note: Adhoc activity tasks (BirthAdoption, Retirement, MarriageRegistration, EmployeeOnboarding, etc.)
    // are NOT in this list because they support BOTH:
    // 1. Runtime notifications (triggered immediately when activity is created/action happens - handled by backend)
    // 2. Scheduled reminders (reminder dropdown to notify before the activity date - configured via preferences)
];
const priorityDisplayMap = {
    [PriorityEnum.None]: 'None',
    [PriorityEnum.High]: 'Most Important',
    [PriorityEnum.Medium]: 'Medium',
    [PriorityEnum.Low]: 'Low'
};
// Map timing settings to a readable string (matches reminder options format)
export const formatTimingSetting = (timing) => {
    if (timing.isDayItself) {
        return 'On the day';
    }
    if (timing.monthsBefore > 0) {
        return `${timing.monthsBefore} ${timing.monthsBefore === 1 ? 'month' : 'months'}`;
    }
    if (timing.weeksBefore > 0) {
        return `${timing.weeksBefore} ${timing.weeksBefore === 1 ? 'week' : 'weeks'}`;
    }
    if (timing.daysBefore > 0) {
        return `${timing.daysBefore} ${timing.daysBefore === 1 ? 'day' : 'days'}`;
    }
    return 'On the day';
};
// Convert timing string back to timing settings
export const parseTimingString = (value) => {
    if (value === 'On the day' || value.toLowerCase().includes('day itself')) {
        return { monthsBefore: 0, weeksBefore: 0, daysBefore: 0, isDayItself: true };
    }
    const match = value.match(/(\d+)\s*(month|week|day)/i);
    if (match) {
        const num = parseInt(match[1]);
        const unit = match[2].toLowerCase();
        if (unit === 'month') {
            return { monthsBefore: num, weeksBefore: 0, daysBefore: 0, isDayItself: false };
        }
        if (unit === 'week') {
            return { monthsBefore: 0, weeksBefore: num, daysBefore: 0, isDayItself: false };
        }
        if (unit === 'day') {
            return { monthsBefore: 0, weeksBefore: 0, daysBefore: num, isDayItself: false };
        }
    }
    return { monthsBefore: 0, weeksBefore: 0, daysBefore: 0, isDayItself: true };
};
// Map API preferences to component categories
export const mapPreferencesToCategories = (preferences, module, icon, title, t) => {
    const modulePreferences = preferences.filter((p) => p.module === module);
    // Define notification types for each module
    let notificationTypes = [];
    if (module === NotificationModule.Events) {
        notificationTypes = [
            EventNotificationType.EventCreated,
            EventNotificationType.EventDeadline,
            EventNotificationType.EventRescheduled
        ];
    }
    else if (module === NotificationModule.Tasks) {
        notificationTypes = [
            TaskNotificationType.TaskAssigned,
            TaskNotificationType.TaskDeadlineApproaching,
            TaskNotificationType.TaskCompleted
        ];
    }
    else if (module === NotificationModule.Activities) {
        notificationTypes = [
            ActivityNotificationType.BirthdayCelebration,
            ActivityNotificationType.WorkAnniversary,
            ActivityNotificationType.WeddingAnniversary,
            ActivityNotificationType.BirthAdoption,
            ActivityNotificationType.Retirement,
            ActivityNotificationType.MarriageRegistration,
            ActivityNotificationType.EmployeeOnboarding,
            ActivityNotificationType.EmployeeDeparture,
            ActivityNotificationType.EmployeeDeath,
            ActivityNotificationType.EmployeeIllness,
            ActivityNotificationType.CompanyAnniversary
        ];
    }
    // Helper to get priority label
    const getPriorityLabel = (priority) => {
        switch (priority) {
            case PriorityEnum.High:
                return t('priority.high') || 'Most Important';
            case PriorityEnum.Medium:
                return t('priority.medium') || 'Medium';
            case PriorityEnum.Low:
                return t('priority.low') || 'Low';
            default:
                return t('priority.none') || 'None';
        }
    };
    // For tasks, create separate entries for TaskDeadlineApproaching per priority
    const settings = [];
    notificationTypes.forEach((notificationType) => {
        if (module === NotificationModule.Tasks && notificationType === TaskNotificationType.TaskDeadlineApproaching) {
            // Create settings for each priority level
            [PriorityEnum.High, PriorityEnum.Medium, PriorityEnum.Low].forEach((priority) => {
                // Find preference matching both notificationType and taskPriority
                // Backend sends priority as 0,1,2 which we normalize to High(1), Medium(2), Low(3)
                const preference = modulePreferences.find((p) => {
                    // Ensure both notificationType and taskPriority match exactly
                    return p.notificationType === notificationType && p.taskPriority === priority;
                });
                const reminderValues = preference?.timingSettings && preference.timingSettings.length > 0
                    ? preference.timingSettings.map((ts) => formatTimingSetting({
                        monthsBefore: ts.monthsBefore,
                        weeksBefore: ts.weeksBefore,
                        daysBefore: ts.daysBefore,
                        isDayItself: ts.isDayItself
                    }))
                    : ['3 days'];
                const notifTypeLabel = t(`notificationTypes.${notificationType}`) || NotificationTypeDisplay[notificationType] || notificationType;
                settings.push({
                    id: `${module}-${notificationType}-${priority}`,
                    label: `${notifTypeLabel} (${getPriorityLabel(priority)})`,
                    enabled: preference?.isEnabled || false,
                    hasReminder: true,
                    reminderValues,
                    preferenceId: preference?.id,
                    notificationType: notificationType,
                    taskPriority: priority,
                    companyIds: preference?.companyIds || [],
                    designations: preference?.designations || null
                });
            });
        }
        else {
            const preference = modulePreferences.find((p) => p.notificationType === notificationType);
            // Runtime-triggered notifications don't need reminders
            const isRuntimeTriggered = RUNTIME_TRIGGERED_NOTIFICATIONS.includes(notificationType);
            // Only compute reminder values for notifications that need them
            const reminderValues = isRuntimeTriggered
                ? undefined
                : preference?.timingSettings && preference.timingSettings.length > 0
                    ? preference.timingSettings.map((ts) => formatTimingSetting({
                        monthsBefore: ts.monthsBefore,
                        weeksBefore: ts.weeksBefore,
                        daysBefore: ts.daysBefore,
                        isDayItself: ts.isDayItself
                    }))
                    : ['3 days'];
            const notifTypeLabel = t(`notificationTypes.${notificationType}`) || NotificationTypeDisplay[notificationType] || notificationType;
            settings.push({
                id: `${module}-${notificationType}`,
                label: notifTypeLabel,
                enabled: preference?.isEnabled || false,
                hasReminder: !isRuntimeTriggered,
                reminderValues,
                preferenceId: preference?.id,
                notificationType: notificationType,
                taskPriority: preference?.taskPriority || null,
                companyIds: preference?.companyIds || [],
                designations: preference?.designations || null
            });
        }
    });
    const enabledCount = settings.filter((s) => s.enabled).length;
    const anyEnabled = settings.some((s) => s.enabled);
    return {
        id: module.toLowerCase(),
        icon,
        title,
        enabled: anyEnabled,
        enabledCount,
        totalCount: settings.length,
        settings
    };
};
